
import Vue from 'vue';
import { 
  getCurrentDatabaseSource, 
  setDatabaseSource, 
  getDatabaseSwitchStatus,
  testDatabaseConnection,
  DATABASE_SOURCES,
  DatabaseSource,
  DatabaseSwitchStatus,
  DatabaseConnectionTest
} from '@/api';

export default Vue.extend({
  name: 'DatabaseSelector',
  
  data() {
    return {
      canUseSwitch: false,
      currentSource: getCurrentDatabaseSource() as DatabaseSource,
      selectedSource: getCurrentDatabaseSource() as DatabaseSource,
      showDialog: false,
      loading: true,
      switching: false,
      testing: false,
      connectionTestResult: null as DatabaseConnectionTest | null,
      statusInfo: null as DatabaseSwitchStatus | null
    };
  },

  computed: {
    currentSourceLabel(): string {
      return this.currentSource === DATABASE_SOURCES.PROD_REPLICA 
        ? 'PROD DATA' 
        : 'STAGING';
    },
    
    indicatorColor(): string {
      return this.currentSource === DATABASE_SOURCES.PROD_REPLICA 
        ? '#FF5722' 
        : '#4CAF50';
    },
    
    indicatorClass(): string {
      return this.currentSource === DATABASE_SOURCES.PROD_REPLICA 
        ? 'prod-replica-active' 
        : 'staging-active';
    }
  },

  async created() {
    await this.checkDatabaseSwitchStatus();
    
    // Listen for database source changes from other components
    window.addEventListener('database-source-changed', this.handleSourceChange as EventListener);
  },

  beforeDestroy() {
    window.removeEventListener('database-source-changed', this.handleSourceChange as EventListener);
  },

  methods: {
    handleSourceChange(event: Event) {
      const customEvent = event as CustomEvent<DatabaseSource>;
      this.currentSource = customEvent.detail;
      this.selectedSource = customEvent.detail;
    },

    async checkDatabaseSwitchStatus() {
      this.loading = true;
      try {
        const status = await getDatabaseSwitchStatus();
        this.statusInfo = status;
        this.canUseSwitch = status.canUseDatabaseSwitch;
        
        // If user can't use the switch but was on prod-replica, reset to staging
        if (!this.canUseSwitch && this.currentSource === DATABASE_SOURCES.PROD_REPLICA) {
          setDatabaseSource(DATABASE_SOURCES.STAGING);
          this.currentSource = DATABASE_SOURCES.STAGING;
          this.selectedSource = DATABASE_SOURCES.STAGING;
        }
      } catch (error) {
        console.error('Failed to check database switch status:', error);
        this.canUseSwitch = false;
      } finally {
        this.loading = false;
      }
    },

    async testConnection() {
      this.testing = true;
      this.connectionTestResult = null;
      
      try {
        const result = await testDatabaseConnection(this.selectedSource);
        this.connectionTestResult = result;
      } catch (error) {
        this.connectionTestResult = {
          success: false,
          message: (error as Error).message || 'Connection test failed',
          source: this.selectedSource
        };
      } finally {
        this.testing = false;
      }
    },

    cancelDialog() {
      this.showDialog = false;
      this.selectedSource = this.currentSource;
      this.connectionTestResult = null;
    },

    async applyChange() {
      if (this.selectedSource === this.currentSource) {
        this.showDialog = false;
        return;
      }

      this.switching = true;
      
      try {
        // First test the connection
        const testResult = await testDatabaseConnection(this.selectedSource);
        
        if (!testResult.success) {
          this.connectionTestResult = testResult;
          return;
        }

        // Set the new source
        setDatabaseSource(this.selectedSource);
        this.currentSource = this.selectedSource;
        
        // Close dialog
        this.showDialog = false;
        
        // Reload the page to ensure all data is refreshed from the new source
        window.location.reload();
      } catch (error) {
        this.connectionTestResult = {
          success: false,
          message: (error as Error).message || 'Failed to switch database',
          source: this.selectedSource
        };
      } finally {
        this.switching = false;
      }
    }
  }
});
